import React, { useState } from "react";
import { Box, Button, Divider, Paper, Typography } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";

import DynamicForm from "../../components/form/DynamicForm";
import { getInvoiceInputs } from "../../utils/invoice.inputs";
import {
  generateInvoice,
  validateInvoiceMonth,
} from "../../services/api/invoice";
import { fetchTenantListByInvoiceDate } from "../../services/api/tenant";
import { currentMonthDateToDDMMYYYY } from "../../utils/dateUtils";
import { showToast } from "../../store/slices/toastSlice";
import {
  CREATE_UPDATE_BUTTON,
  RESET_CANCEL_BUTTON,
} from "../../styles/ButtonStyles";
import TenantElectricityConsumption from "./TenantElectricityConsumption";

export default function GenerateInvoice() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const initialFormState = {
    invoice_date: currentMonthDateToDDMMYYYY(),
  };

  const [invoiceData, setInvoiceData] = useState(initialFormState);
  const [tenants, setTenants] = useState([]); // List of tenants
  const [tenantConsumption, setTenantConsumption] = useState({}); // Units input
  const [showTenants, setShowTenants] = useState(false); // To show tenant section

  const handleChange = (e) => {
    const { name, value } = e.target;
    setInvoiceData((prev) => ({ ...prev, [name]: value }));
  };

  const handleConsumptionChange = (e, tenantId) => {
    const { value } = e.target;
    setTenantConsumption((prev) => ({
      ...prev,
      [tenantId]: value,
    }));
  };

  // Handle "Next" button click (validation + show tenant input)
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      // Validate invoice month
      const validationResponse = await validateInvoiceMonth(
        invoiceData.invoice_date
      );

      if (
        validationResponse.responseCode === 200 &&
        validationResponse.invoiceExists
      ) {
        dispatch(
          showToast({
            message: "Invoice for this month already exists.",
            severity: "error",
          })
        );
        return;
      }

      // Fetch tenants
      // Fetch tenants
      const tenantResponse = await fetchTenantListByInvoiceDate(
        invoiceData.invoice_date
      );
      const tenantArray = Array.isArray(tenantResponse.data)
        ? tenantResponse.data
        : [];

      // Split tenants into two categories
      const tenantsWithConsumption = tenantArray.filter(
        (tenant) => tenant.electricity_per_unit_charge != null
      );

      const tenantsWithoutConsumption = tenantArray.filter(
        (tenant) => tenant.electricity_per_unit_charge == null
      );

      setTenants(tenantArray); // Keep all tenants for payload

      // Initialize consumption only for tenants who need it
      const initialConsumption = tenantsWithConsumption.reduce(
        (acc, tenant) => {
          acc[tenant.id] = ""; // user will input units
          return acc;
        },
        {}
      );

      setTenantConsumption(initialConsumption);
      setShowTenants(true);
    } catch (error) {
      dispatch(
        showToast({
          message: error.message || "An error occurred.",
          severity: "error",
        })
      );
    }
  };

  // Final invoice generation (after user adds consumption)
  const handleGenerateInvoice = async () => {
    try {
      const invoicePayload = {
        ...invoiceData,
        tenants: Object.fromEntries(
          Object.entries(tenantConsumption).map(([tenantId, units]) => [
            tenantId,
            parseFloat(units) || 0,
          ])
        ),
      };

      console.log("@@@@@@@@@@ invoicePayload :", invoicePayload);
      const response = await generateInvoice(invoicePayload);
      if (response.responseCode === 200) {
        dispatch(showToast({ message: response.message, severity: "success" }));
        localStorage.setItem("generateInvoiceSuccess", response.message);
        navigate("/invoice-list");
      }
    } catch (error) {
      dispatch(showToast({ message: error.message, severity: "error" }));
    }
  };

  const handleCancelOrReset = () => {
    setInvoiceData(initialFormState);
    setTenantConsumption({});
    setShowTenants(false);
  };

  const formInputs = getInvoiceInputs();

  return (
    <>
      {/* Invoice Form */}
      <Box
        sx={{
          width: "100%",
          mt: 4,
          px: { xs: 2, sm: 3 },
        }}
      >
        <Paper
          elevation={3}
          sx={{
            width: "100%",
            borderRadius: 3,
            p: { xs: 3, sm: 4 },
            backgroundColor: "#fff",
            boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
          }}
        >
          <Typography
            variant="h5"
            fontWeight="bold"
            sx={{ mb: 2, color: "#333" }}
          >
            Generate Invoice
          </Typography>

          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ mt: 1, mb: 1 }}
          >
            Set the Invoice date and fill out the tenant electricity consumption
            to generate the invoice.
          </Typography>

          <Divider sx={{ mb: 5 }} />

          <Box component="form" onSubmit={handleSubmit}>
            <DynamicForm
              inputs={formInputs}
              values={invoiceData}
              onChange={handleChange}
            />

            <Divider sx={{ mt: 5 }} />

            <Box sx={{ display: "flex", justifyContent: "flex-start", mt: 2 }}>
              <Button
                type="submit"
                variant="contained"
                sx={CREATE_UPDATE_BUTTON}
              >
                Next
              </Button>

              <Button
                type="button"
                variant="text"
                onClick={handleCancelOrReset}
                sx={RESET_CANCEL_BUTTON}
              >
                Reset
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>

      {/* Tenant List with Electricity Input */}
      {showTenants && (
        <TenantElectricityConsumption
          tenants={tenants}
          tenantConsumption={tenantConsumption}
          handleConsumptionChange={handleConsumptionChange}
          handleGenerateInvoice={handleGenerateInvoice}
        />
      )}
    </>
  );
}
